import React from "react"
import {Document, Page, StyleSheet, Text, View} from "@react-pdf/renderer"
import {format} from "date-fns"

import type {CaseNote} from "@indebted/api"

export type CasePDFAccount = {
	AccountRef: string
	Balance: string
	Resolution: string | null
}

export type CasePDFData = {
	CaseType: string
	CustomerName: string
	CustomerRef: string
	SubmittedAt: string
	ExpiryDate: string | null
	Accounts: CasePDFAccount[]
	Description?: string
}

const styles = StyleSheet.create({
	page: {
		padding: 40,
		fontFamily: "Helvetica",
		fontSize: 10,
		color: "#111827",
	},
	pageHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-start",
		marginBottom: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: "#E5E7EB",
		borderBottomStyle: "solid",
	},
	caseTypeTag: {
		backgroundColor: "#FEF3C7",
		color: "#92400E",
		borderRadius: 4,
		paddingLeft: 8,
		paddingRight: 8,
		paddingTop: 3,
		paddingBottom: 3,
		fontSize: 8,
		fontFamily: "Helvetica-Bold",
		alignSelf: "flex-start",
		marginBottom: 8,
	},
	customerName: {
		fontSize: 18,
		fontFamily: "Helvetica-Bold",
		marginBottom: 3,
	},
	customerRef: {
		fontSize: 10,
		color: "#6B7280",
	},
	generatedAt: {
		fontSize: 8,
		color: "#9CA3AF",
	},
	sectionTitle: {
		fontSize: 11,
		fontFamily: "Helvetica-Bold",
		marginBottom: 10,
	},
	accountTable: {
		marginBottom: 24,
	},
	accountRow: {
		flexDirection: "row",
		paddingTop: 7,
		paddingBottom: 7,
		borderBottomWidth: 1,
		borderBottomColor: "#F3F4F6",
		borderBottomStyle: "solid",
	},
	accountRef: {
		flex: 2,
		fontSize: 10,
		fontFamily: "Helvetica-Bold",
	},
	accountBalance: {
		flex: 2,
		fontSize: 10,
		color: "#374151",
	},
	accountResolution: {
		flex: 2,
		fontSize: 10,
		color: "#6B7280",
	},
	accountTableHeader: {
		flexDirection: "row",
		paddingBottom: 6,
		borderBottomWidth: 1,
		borderBottomColor: "#E5E7EB",
		borderBottomStyle: "solid",
		marginBottom: 2,
	},
	accountTableHeaderCell: {
		flex: 2,
		fontSize: 7,
		color: "#9CA3AF",
		fontFamily: "Helvetica-Bold",
		textTransform: "uppercase",
	},
	noteCard: {
		borderWidth: 1,
		borderColor: "#E5E7EB",
		borderStyle: "solid",
		borderRadius: 4,
		padding: 12,
		marginBottom: 10,
	},
	noteLabel: {
		fontSize: 10,
		fontFamily: "Helvetica-Bold",
		marginBottom: 3,
	},
	noteMeta: {
		fontSize: 8,
		color: "#6B7280",
		marginBottom: 10,
	},
	attachmentsLabel: {
		fontSize: 7,
		color: "#9CA3AF",
		fontFamily: "Helvetica-Bold",
		textTransform: "uppercase",
		marginBottom: 3,
		marginTop: 6,
	},
	attachmentItem: {
		fontSize: 9,
		color: "#6B7280",
		marginBottom: 2,
	},
	fieldRow: {
		marginBottom: 6,
	},
	fieldName: {
		fontSize: 7,
		color: "#9CA3AF",
		fontFamily: "Helvetica-Bold",
		textTransform: "uppercase",
		marginBottom: 2,
	},
	fieldContent: {
		fontSize: 9,
		color: "#374151",
	},
	description: {
		fontSize: 10,
		color: "#374151",
		marginBottom: 20,
		lineHeight: 1.5,
	},
	emptyText: {
		fontSize: 10,
		color: "#9CA3AF",
	},
})

type Props = {
	data: CasePDFData
	notes: CaseNote[]
}

export function CasePDFDocument({data, notes}: Props) {
	const openedDate = format(new Date(data.SubmittedAt), "d MMM yyyy")

	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<View style={styles.pageHeader}>
					<View>
						<Text style={styles.caseTypeTag}>{data.CaseType}</Text>
						<Text style={styles.customerName}>{data.CustomerName}</Text>
						<Text style={styles.customerRef}>{data.CustomerRef}</Text>
					</View>
					<Text style={styles.generatedAt}>Opened {openedDate}</Text>
				</View>

				{data.Description?.trim() !== "" && data.Description && (
					<Text style={styles.description}>{data.Description}</Text>
				)}

				{data.Accounts.length > 0 && (
					<View style={styles.accountTable}>
						<Text style={styles.sectionTitle}>Accounts ({data.Accounts.length})</Text>
						<View style={styles.accountTableHeader}>
							<Text style={styles.accountTableHeaderCell}>Reference</Text>
							<Text style={styles.accountTableHeaderCell}>Balance</Text>
						</View>
						{data.Accounts.map((a) => (
							<View key={a.AccountRef} style={styles.accountRow}>
								<Text style={styles.accountRef}>{a.AccountRef}</Text>
								<Text style={styles.accountBalance}>{a.Balance}</Text>
							</View>
						))}
					</View>
				)}

				<Text style={styles.sectionTitle}>Notes ({notes.length})</Text>

				{notes.length === 0 && <Text style={styles.emptyText}>No notes on this case.</Text>}

				{notes.map((note) => (
					<View key={note.ID} style={styles.noteCard}>
						<Text style={styles.noteLabel}>{note.Label}</Text>
						<Text style={styles.noteMeta}>{format(new Date(note.CreatedAt), "d MMM yyyy, HH:mm")}</Text>
						{note.Form.Fields.filter((f) => f.Content).map((field) => (
							<View key={field.Name} style={styles.fieldRow}>
								<Text style={styles.fieldName}>{field.Name}</Text>
								<Text style={styles.fieldContent}>{field.Content}</Text>
							</View>
						))}
						{note.Uploads.length > 0 && (
							<View>
								<Text style={styles.attachmentsLabel}>Attachments</Text>
								{note.Uploads.map((upload) => (
									<Text key={upload.ID} style={styles.attachmentItem}>
										• {upload.FileName}
									</Text>
								))}
							</View>
						)}
					</View>
				))}
			</Page>
		</Document>
	)
}
